import type { ReactNode } from "react";
import React from "react";

import { connect } from "react-redux";
import styled from "styled-components";

import { gridSpacing } from "@utils";

import BreadCrumbs from "../BreadCrumbs/BreadCrumbs";
import Button from "../Button/Button";
import type { ButtonStatus, ButtonType } from "../Button/Button";
import Card from "../Card/Card";
import DocumentationButton from "../DocumentationButton/DocumentationButton";
import GraphicCallout from "../GraphicCallout/GraphicCallout";
import Loading from "../Loading/Loading";

interface Route {
  path?: string;
  breadcrumb: ReactNode;
  onClick?: () => void;
}

const Header = styled.div<{ $hasBreadCrumbs: boolean }>`
  padding: ${({ $hasBreadCrumbs }) =>
      $hasBreadCrumbs ? gridSpacing[2] : gridSpacing[7]}px
    0 ${gridSpacing[6]}px;
  font-size: 24px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const CardInner = styled.div<{ $padding: number }>`
  padding: ${({ $padding }) => $padding}px;
`;

const BackContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  height: 42px;
`;

interface PageProps {
  header?: string | ReactNode;
  headerStyle?: React.CSSProperties;
  children?: ReactNode;
  buttonText?: string;
  buttonIcon?: string;
  buttonFunc?: () => void;
  buttonType?: ButtonType;
  buttonDisabled?: boolean;
  buttonStatus?: ButtonStatus;
  buttonTestId?: string;
  buttonAffirmationText?: string;
  secondButtonText?: string;
  secondButtonIcon?: string;
  secondButtonFunc?: () => void;
  secondButtonType?: ButtonType;
  secondButtonDisabled?: boolean;
  secondButtonStatus?: ButtonStatus;
  secondButtonAffirmationText?: string;
  secondButtonTestId?: string;
  buttonsContainer?: ReactNode;
  padding?: number;
  style?: React.CSSProperties;
  outerStyle?: React.CSSProperties;
  backFunc?: () => void;
  breadCrumbs?: Array<Route>;
  isLoading?: boolean;
  errorMessage?: string;
  documentationLink?: string;
  documentationUrl?: string;
}

function Page({
  header,
  headerStyle,
  children,
  buttonText,
  buttonIcon,
  buttonFunc,
  buttonType,
  buttonDisabled = false,
  buttonStatus,
  buttonTestId,
  buttonAffirmationText,
  secondButtonText,
  secondButtonIcon,
  secondButtonFunc,
  secondButtonType,
  secondButtonDisabled,
  secondButtonStatus,
  secondButtonAffirmationText,
  secondButtonTestId,
  buttonsContainer,
  padding = 0,
  style,
  outerStyle,
  backFunc,
  breadCrumbs,
  isLoading = false,
  errorMessage,
  documentationLink,
  documentationUrl,
}: PageProps) {
  const [baseRoute, ...additionalRoutes] = breadCrumbs || [];

  const renderContent = () => {
    if (errorMessage) {
      return <GraphicCallout error="issue" subheader={errorMessage} />;
    } else if (isLoading) {
      return <Loading center />;
    } else {
      return children;
    }
  };

  return (
    <>
      {breadCrumbs && baseRoute && (
        <BreadCrumbs
          baseRoute={baseRoute}
          routes={additionalRoutes}
          style={{ paddingTop: gridSpacing[7] }}
        />
      )}
      {header && (
        <Header $hasBreadCrumbs={!!breadCrumbs} style={headerStyle}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              width: "100%",
            }}
          >
            {typeof header === "string" ? <h2>{header}</h2> : header}
            {documentationLink && documentationUrl && (
              <DocumentationButton ml docRoute={documentationLink} />
            )}
          </div>
          {!isLoading && !errorMessage ? (
            <ButtonContainer>
              {buttonFunc && (
                <Button
                  text={buttonText}
                  icon={buttonIcon}
                  onClick={buttonFunc}
                  disabled={buttonDisabled}
                  variant={buttonType}
                  status={buttonStatus}
                  affirmationText={buttonAffirmationText}
                  testId={buttonTestId}
                />
              )}
              {secondButtonFunc && (
                <Button
                  style={{ marginLeft: gridSpacing[4] }}
                  text={secondButtonText}
                  icon={secondButtonIcon}
                  onClick={secondButtonFunc}
                  disabled={secondButtonDisabled}
                  variant={secondButtonType}
                  status={secondButtonStatus}
                  affirmationText={secondButtonAffirmationText}
                  testId={secondButtonTestId}
                />
              )}
              {buttonsContainer}
            </ButtonContainer>
          ) : (
            <div style={{ height: 42 }} />
          )}
        </Header>
      )}
      <Card style={{ boxShadow: "none", ...outerStyle }}>
        <CardInner style={style} $padding={padding}>
          {renderContent()}
          <BackContainer>
            {backFunc && (
              <Button variant="secondary" onClick={backFunc} text="Back" />
            )}
          </BackContainer>
        </CardInner>
      </Card>
    </>
  );
}

const mapStateToProps = (state: {
  main: {
    documentationUrl?: string;
  };
}) => {
  return {
    documentationUrl: state?.main?.documentationUrl,
  };
};

export default connect(mapStateToProps)(Page);
